import React, { useState, useEffect } from 'react';
import { API } from '../../../../common/Constants';
import useFetch from '../../../../hooks/useFetch';
import { getAPIHeaders, getFormattedDateString } from '../../../../common/Utils';
import CustomModal from '../../../../core/common/customModal';
import ImageWithBasePath from '../../../../core/common/imageWithBasePath';
import { Review } from '../../../../types/dataModel';
import PreLoader from '../../../../core/common/preLoader';

interface CourseReviewsProps {
  show: boolean;
  onClose: () => void;
  course: any;
  onError: (message: string, heading: string) => void;
}

const CourseReviews: React.FC<CourseReviewsProps> = ({ 
  show, 
  onClose, 
  course, 
  onError 
}) => {
  const { isLoading: isLoadingReviews, callFetch: fetchReviews } = useFetch();
  const [reviews, setReviews] = useState<Review[]>([]);

  useEffect(() => {
    if (course) {
      setReviews([]);
      const reviewsUrl = API.COMMERCIAL_COURSE_REIVEW.replace(':courseId', course.id.toString());
      fetchReviews(
        reviewsUrl,
        'GET',
        getAPIHeaders(),
        null,
        null
      ).then((reviewsData: any)=>{
        setReviews(reviewsData);
      }).catch((error) => {
        onError(
          error?.response?.data || "Failed to fetch reviews. Please try again later.",
          "Error Loading Reviews"
        );
      });
    }
  }, [course]);

  // Helper function to render star rating
  const renderStarRating = (rating: string) => {
    const ratingNum = parseInt(rating);
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= ratingNum) {
        stars.push(<i key={i} className="ti ti-star-filled text-warning" />);
      } else {
        stars.push(<i key={i} className="ti ti-star text-muted" />);
      }
    }
    return <div className="d-flex align-items-center gap-1">{stars}</div>;
  };

  // Toggle review visibility handler
  const toggleReviewVisibility = (reviewIndex: number) => {
    const review = reviews[reviewIndex];
    const newHiddenState = !review.isHidden;
    
    if (!review.id) {
      onError("Review ID not found", "Error");
      return;
    }

    // Make API call to update review visibility
    const visibilityUrl = API.COMMERCIAL_COURSE_REVIEW_VISIBILITY.replace(':reviewId', review.id.toString());
    const urlWithParams = `${visibilityUrl}?isHidden=${newHiddenState}`;
    
    fetchReviews(
      urlWithParams,
      'POST',
      getAPIHeaders(),
      null,
      null
    ).then(() => {
      // Update local state only after successful API call
      const updatedReviews = [...reviews];
      updatedReviews[reviewIndex] = {
        ...updatedReviews[reviewIndex],
        isHidden: newHiddenState
      };
      setReviews(updatedReviews);
    }).catch((error) => {
      onError(
        error?.response?.data || "Failed to update review visibility. Please try again later.",
        "Error Updating Review"
      );
    });
  };

  const handleClose = () => {
    setReviews([]);
    onClose();
  };

  return (
    <CustomModal
      show={show}
      onClose={handleClose}
      title={
        <div className="d-flex align-items-center">
          <i className="ti ti-star text-warning me-2" />
          <span>User Reviews - {course?.title}</span>
        </div>
      }
      size="xl"
      bodyContent={
        <div className="reviews-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <PreLoader showLoader={isLoadingReviews} />
          {reviews.length > 0 ? (
            <div className="row g-3">
              {reviews.map((review, index) => (
                <div key={index} className="col-12">
                  <div className="card border shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-2">
                            {renderStarRating(review?.rating?.toString() || '0')}
                            <span className="ms-2 badge badge-soft-primary">
                              {review.rating}/5
                            </span>
                          </div>
                          {review.userFullName && (
                            <div className="d-flex align-items-center">
                              <div className="me-2">
                                <ImageWithBasePath
                                  className="rounded-circle"
                                  src="assets/img/profiles/user-default.png"
                                  url={review.profilePictureUrl}
                                  alt={review.userFullName}
                                  height={32}
                                  width={32}
                                />
                              </div>
                              <div className="text-muted small">
                                <div className="fw-medium text-dark">{review.userFullName}</div>
                                {review.createdOn && (
                                  <div>
                                    <i className="ti ti-calendar me-1" />
                                    {getFormattedDateString(review.createdOn)}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className={`badge ${!review.isHidden ? 'badge-soft-success' : 'badge-soft-secondary'}`}>
                            {!review?.isHidden ? 'Public' : 'Internal Only'}
                          </span>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={!review.isHidden}
                              onChange={() => toggleReviewVisibility(index)}
                              id={`visibility-toggle-${index}`}
                              title="Toggle visibility"
                            />
                            <label 
                              className="form-check-label small text-muted" 
                              htmlFor={`visibility-toggle-${index}`}
                            >
                              Visible
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      {review.reviewTitle && (
                        <h6 className="fw-semibold mb-2">
                          {review.reviewTitle}
                        </h6>
                      )}
                      
                      {review.reviewText && (
                        <p className="text-muted mb-0">
                          {review.reviewText}
                        </p>
                      )}
                      
                      {!review.reviewTitle && !review.reviewText && (
                        <p className="text-muted fst-italic mb-0">
                          No detailed review provided.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="ti ti-star-off fs-1 text-muted mb-3 d-block" />
              <h5 className="text-muted">No reviews yet</h5>
              <p className="text-muted">This course hasn't received any reviews yet.</p>
            </div>
          )}
        </div>
      }
      footerContent={
        <div className="d-flex justify-content-between w-100">
          <div className="text-muted small">
            Total Reviews: <strong>{reviews.length}</strong>
            <span className="mx-2">|</span>
            Public: <strong>{reviews.filter(r => !r.isHidden).length}</strong>
            <span className="mx-2">|</span>
            Internal: <strong>{reviews.filter(r => r.isHidden).length}</strong>
          </div>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      }
    />
  );
};

export default CourseReviews;
